// 0130 · OPERADORES Y POLÍTICA DE CAJA (administración de cajas: caja.operadores.gestionar). Por cada
// caja física de mesón (MESON-1…), que se usa desde cualquier puesto:
//  - RESPONSABLES ELEGIBLES (V3): quién puede abrir la caja y ser la responsable de un turno. La
//    responsable es del turno, no del puesto ni fija. Sin responsables configurados, la caja se abre como
//    hoy.
//  - OPERADORAS ELEGIBLES: quién puede cobrar en el turno de otra. Persisten entre turnos; dentro de un
//    turno, la responsable habilita solo a estas. Retirarla la saca también del turno abierto. Una
//    persona puede tener las dos funciones.
//  - POLÍTICA DE CIERRE (caja.politica_cierre.gestionar): con ella, un cierre que cuadra es normal y no
//    espera revisión; uno con diferencia o incidentes queda como excepción de nivel 1 o 2. Los umbrales
//    (material, anulación material, reincidencia por persona y por caja) y los códigos de causa que
//    piden texto se editan aquí; los valores piloto los entrega el servidor (la web no tiene números).
// Nada se borra: se retira con fecha, y la política se guarda por versiones.
// 0132 · la pantalla pasa a llamarse CAJAS (Rodrigo, 2026-11-01: «muy complejo»): primero las cajas de
// la empresa (agregar y cambiar nombre); compartir una caja abierta no pide elegibles, así que los
// elegibles y las cuentas compartidas quedan plegados como opciones avanzadas.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import {
  claves,
  useCandidatosCaja,
  useCuentasCompartidas,
  useElegiblesPunto,
  usePoliticaCierre,
  usePuntosCobro,
} from '../api/consultas';
import type {
  CodigoCausa,
  CodigoSenal,
  ElegibleCaja,
  FuncionElegible,
  PoliticaCierre,
  PuntoCobro,
} from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { enteroCaja } from './MiTurno';
import { NOMBRE_CAUSA, NOMBRE_SENAL } from './textos';

/** Las señales cuya escalada decide la política (las de regla fija suben siempre). */
const SENALES_ESCALABLES: CodigoSenal[] = [
  'MULTIPLE_VOIDS_SAME_OPERATOR',
  'MULTIPLE_VOIDS_SAME_SHIFT',
  'VOID_AFTER_CLOSE',
  'VOID_AFTER_SETTLEMENT',
  'REPEATED_VOID_CUSTOMER',
];

/**
 * V4/V5 · CUENTAS COMPARTIDAS DE PUESTO (legado): la lista explícita, con vigencia, de cuentas que no son
 * de una persona. Mientras están vigentes abren turnos LEGACY_SHARED (aunque la caja tenga responsables
 * configuradas): no habilitan operadoras y la persona responsable es desconocida. Fuera de su vigencia
 * (o retiradas) no abren, ni como personales.
 */
function CuentasCompartidas({ empresaId }: { empresaId: string }) {
  const qc = useQueryClient();
  const lista = useCuentasCompartidas(empresaId);
  const candidatos = useCandidatosCaja(empresaId);
  const [usuarioId, setUsuarioId] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const refrescar = () => qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
  const libres = (candidatos.data ?? []).filter(
    (c) => !(lista.data ?? []).some((x) => x.usuarioId === c.id),
  );
  const elegida = usuarioId && libres.some((c) => c.id === usuarioId) ? usuarioId : '';
  const agregar = useMutation({
    mutationFn: () =>
      api.post('/caja/cuentas-compartidas', {
        empresaId,
        usuarioId: elegida,
        etiqueta: etiqueta.trim(),
      }),
    onSuccess: async () => {
      setUsuarioId('');
      setEtiqueta('');
      await refrescar();
    },
  });
  const retirar = useMutation({
    mutationFn: (id: string) => api.post(`/caja/cuentas-compartidas/${id}/retirar`, {}),
    onSuccess: refrescar,
  });
  return (
    <section className="recuadro" aria-label="Cuentas compartidas de puesto">
      <h3>Cuentas compartidas de puesto (legado)</h3>
      <p className="apagado">
        Cuentas que no son de una persona. Mientras estén vigentes abren turnos legados
        (LEGACY_SHARED), aunque la caja tenga responsables configuradas: no habilitan operadoras y
        la persona responsable queda como desconocida. Retirada de la lista, la cuenta ya no abre
        caja.
      </p>
      {lista.isError && <PanelError error={lista.error} />}
      <table className="grilla compacta" aria-label="Cuentas compartidas">
        <thead>
          <tr>
            <th>Cuenta</th>
            <th>Etiqueta</th>
            <th>Vigente desde</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(lista.data ?? []).map((c) => (
            <tr key={c.cuentaCompartidaId}>
              <td>{c.cuenta}</td>
              <td>{c.etiqueta}</td>
              <td>
                {fechaHora(c.vigenteDesde)}
                {c.vigenteHasta ? ` → ${fechaHora(c.vigenteHasta)}` : ''}
              </td>
              <td className="al-der">
                <button
                  type="button"
                  disabled={retirar.isPending}
                  onClick={() => retirar.mutate(c.cuentaCompartidaId)}
                >
                  Retirar
                </button>
              </td>
            </tr>
          ))}
          {lista.isSuccess && !lista.data.length && (
            <tr>
              <td colSpan={4} className="grilla-vacia">
                Sin cuentas compartidas registradas
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="acciones">
        <label className="campo">
          <span className="etiqueta">Cuenta</span>
          <select
            aria-label="Cuenta compartida a registrar"
            value={elegida}
            onChange={(e) => setUsuarioId(e.target.value)}
          >
            <option value="">— elegir —</option>
            {libres.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre} · {c.correo}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Etiqueta (p. ej. el puesto)</span>
          <input
            aria-label="Etiqueta de la cuenta compartida"
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={!elegida || etiqueta.trim().length < 2 || agregar.isPending}
          onClick={() => agregar.mutate()}
        >
          Registrar como compartida
        </button>
      </div>
      {agregar.isError && <PanelError error={agregar.error} />}
      {retirar.isError && <PanelError error={retirar.error} />}
    </section>
  );
}

const NOMBRE_FUNCION: Record<FuncionElegible, string> = {
  RESPONSABLE: 'Responsable',
  OPERADOR: 'Operadora',
  OPERADOR_EXCEPCIONAL: 'Operadora excepcional',
};

export function OperadoresCaja() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const puntos = usePuntosCobro(empresaId);
  const compartibles = (puntos.data ?? []).filter((p) => p.compartible !== false && p.activo);
  const [puntoId, setPuntoId] = useState('');
  const elegido = puntoId || compartibles[0]?.id || '';
  return (
    <div className="pantalla consulta caja-compartida" data-pantalla="operadores-caja">
      <header className="encabezado-entidad">
        <span className="ruta-migas">Ventas › Caja › Cajas</span>
        <h2>Cajas</h2>
      </header>
      <p className="apagado">
        Cada mesón está en un puesto. Quien va a cobrar elige su puesto en «Mi turno de caja»: si la
        caja está cerrada la abre (y es su responsable: la cierra y la arquea); si ya está abierta,
        la comparte y cobra en ella a su nombre, con un solo arqueo. La caja de tesorería es de su
        titular: lo que cobra en efectivo o tarjeta entra ahí.
      </p>
      {puntos.isError && <PanelError error={puntos.error} />}
      <CajasEmpresa empresaId={empresaId} puedeEditar={puede('caja.punto.gestionar')} />
      {compartibles.length > 0 && (
        <>
          <label className="campo">
            <span className="etiqueta">Política de cierre de la caja</span>
            <select aria-label="Caja" value={elegido} onChange={(e) => setPuntoId(e.target.value)}>
              {compartibles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo} · {p.nombre}
                  {p.ocupadoPor ? ` (abierta: ${p.ocupadoPor})` : ''}
                </option>
              ))}
            </select>
          </label>
          {elegido && (
            <PoliticaDeCaja
              empresaId={empresaId}
              puntoId={elegido}
              puedeEditar={puede('caja.politica_cierre.gestionar')}
            />
          )}
          <details className="caja-avanzado">
            <summary>Opciones avanzadas: elegibles y cuentas compartidas de puesto</summary>
            <p className="apagado">
              No hacen falta para compartir una caja. Sirven para limitar quién puede abrirla o para
              marcar las cuentas que no son de una persona (call1, call2…).
            </p>
            {elegido && <ElegiblesDeCaja empresaId={empresaId} puntoId={elegido} />}
            <CuentasCompartidas empresaId={empresaId} />
          </details>
        </>
      )}
    </div>
  );
}

/**
 * 0132/0133 · LAS CAJAS de la empresa (Rodrigo, 2026-11-01: «solo quiero que existan esas 4 cajas:
 * TESORERIA, MESON-1, MESON-2, MESON-3, con nombre; tesorería asociada directamente a Daniela; puesto 1
 * mesón 1, puesto 2 mesón 2, puesto 3 mesón 3»): puesto, código, nombre, titular de tesorería y quién la
 * tiene abierta o la comparte. Con caja.punto.gestionar se agrega una caja (mesón o la de tesorería, una
 * por empresa) y se configura (nombre, puesto del mesón, titular de tesorería; el código no cambia: lo
 * llevan sus turnos). El motivo es fijo: queda la auditoría. Las cajas personales ya no se listan.
 */
function CajasEmpresa({ empresaId, puedeEditar }: { empresaId: string; puedeEditar: boolean }) {
  const qc = useQueryClient();
  const puntos = usePuntosCobro(empresaId);
  const candidatos = useCandidatosCaja(empresaId);
  const personas = candidatos.data ?? [];
  const cajas = (puntos.data ?? [])
    .filter((p) => p.tipo !== 'PERSONAL' && !p.usuarioId)
    .sort((a, b) => ordenCaja(a) - ordenCaja(b) || a.codigo.localeCompare(b.codigo));
  const hayTesoreria = cajas.some((p) => p.tipo === 'TESORERIA');
  const [editando, setEditando] = useState<{
    id: string;
    nombre: string;
    puesto: string;
    titularId: string;
  } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [puesto, setPuesto] = useState('');
  const [titularId, setTitularId] = useState('');
  const [tipo, setTipo] = useState<'MESON' | 'TESORERIA'>('MESON');
  const refrescar = () => qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
  const numero = (t: string) => (/^\d{1,2}$/.test(t.trim()) ? Number(t.trim()) : null);
  const configurar = useMutation({
    mutationFn: ({ p, e }: { p: PuntoCobro; e: NonNullable<typeof editando> }) =>
      api.post(`/caja/puntos/${p.id}/configurar`, {
        ...(e.nombre.trim() !== p.nombre ? { nombre: e.nombre.trim() } : {}),
        ...(p.tipo === 'MESON' && numero(e.puesto) !== (p.puesto ?? null)
          ? { puesto: numero(e.puesto) }
          : {}),
        ...(p.tipo === 'TESORERIA' && (e.titularId || null) !== (p.titularId ?? null)
          ? { titularId: e.titularId || null }
          : {}),
      }),
    onSuccess: async () => {
      setEditando(null);
      await refrescar();
    },
  });
  const agregar = useMutation({
    mutationFn: () =>
      api.post('/caja/puntos', {
        empresaId,
        codigo: codigo.trim().toUpperCase(),
        nombre: nombre.trim(),
        tipo,
        ...(tipo === 'MESON' && numero(puesto) ? { puesto: numero(puesto) } : {}),
        ...(tipo === 'TESORERIA' && titularId ? { titularId } : {}),
        motivo: 'Alta de caja',
      }),
    onSuccess: async () => {
      setCodigo('');
      setNombre('');
      setPuesto('');
      setTitularId('');
      setTipo('MESON');
      await refrescar();
    },
  });
  const columnas = puedeEditar ? 6 : 5;
  return (
    <section className="recuadro" aria-label="Cajas de la empresa">
      <h3>Cajas de la empresa</h3>
      <table className="grilla compacta" aria-label="Cajas">
        <thead>
          <tr>
            <th>Puesto</th>
            <th>Código</th>
            <th>Nombre</th>
            <th>Titular</th>
            <th>Estado</th>
            {puedeEditar && <th />}
          </tr>
        </thead>
        <tbody>
          {cajas.map((p) => {
            const e = editando?.id === p.id ? editando : null;
            const poner = (c: Partial<NonNullable<typeof editando>>) =>
              setEditando({ ...e!, ...c });
            const valido =
              !!e &&
              e.nombre.trim().length >= 2 &&
              (p.tipo !== 'MESON' || !e.puesto.trim() || numero(e.puesto) !== null);
            return (
              <tr key={p.id} data-caja={p.codigo}>
                <td>
                  {p.tipo === 'TESORERIA' ? (
                    'Tesorería'
                  ) : e ? (
                    <input
                      aria-label={`Puesto de ${p.codigo}`}
                      inputMode="numeric"
                      size={3}
                      value={e.puesto}
                      onChange={(x) => poner({ puesto: x.target.value })}
                    />
                  ) : p.puesto ? (
                    `Puesto ${p.puesto}`
                  ) : (
                    '—'
                  )}
                </td>
                <td className="mono">{p.codigo}</td>
                <td>
                  {e ? (
                    <input
                      aria-label={`Nombre de ${p.codigo}`}
                      autoFocus
                      value={e.nombre}
                      onChange={(x) => poner({ nombre: x.target.value })}
                      onKeyDown={(x) => {
                        if (x.key === 'Enter' && valido) configurar.mutate({ p, e });
                        if (x.key === 'Escape') setEditando(null);
                      }}
                    />
                  ) : (
                    p.nombre
                  )}
                </td>
                <td>
                  {p.tipo !== 'TESORERIA' ? (
                    '—'
                  ) : e ? (
                    <select
                      aria-label={`Titular de ${p.codigo}`}
                      value={e.titularId}
                      onChange={(x) => poner({ titularId: x.target.value })}
                    >
                      <option value="">— sin titular —</option>
                      {personas.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (p.titular ?? '—')
                  )}
                </td>
                <td>
                  {!p.activo
                    ? 'Inactiva'
                    : p.ocupadoPor
                      ? `Abierta por ${p.ocupadoPor}${
                          p.compartenCon?.length
                            ? ` · comparte: ${p.compartenCon.map((c) => c.nombre).join(', ')}`
                            : ''
                        }`
                      : 'Cerrada'}
                </td>
                {puedeEditar && (
                  <td className="al-der">
                    {e ? (
                      <>
                        <button
                          type="button"
                          className="primario"
                          disabled={!valido || configurar.isPending}
                          onClick={() => configurar.mutate({ p, e })}
                        >
                          Guardar
                        </button>{' '}
                        <button type="button" onClick={() => setEditando(null)}>
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setEditando({
                            id: p.id,
                            nombre: p.nombre,
                            puesto: p.puesto ? String(p.puesto) : '',
                            titularId: p.titularId ?? '',
                          })
                        }
                      >
                        Configurar
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {puntos.isSuccess && !cajas.length && (
            <tr>
              <td colSpan={columnas} className="grilla-vacia">
                La empresa no tiene cajas
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {configurar.isError && <PanelError error={configurar.error} />}
      {puedeEditar && (
        <div className="acciones">
          <label className="campo">
            <span className="etiqueta">Tipo</span>
            <select
              aria-label="Tipo de la caja nueva"
              value={tipo}
              onChange={(x) => {
                const t = x.target.value as 'MESON' | 'TESORERIA';
                setTipo(t);
                if (t === 'TESORERIA') {
                  setCodigo('TESORERIA');
                  setNombre('Tesorería');
                }
              }}
            >
              <option value="MESON">Mesón</option>
              {!hayTesoreria && <option value="TESORERIA">Tesorería</option>}
            </select>
          </label>
          {tipo === 'MESON' ? (
            <label className="campo">
              <span className="etiqueta">Puesto</span>
              <input
                aria-label="Puesto de la caja nueva"
                inputMode="numeric"
                size={3}
                placeholder="2"
                value={puesto}
                onChange={(x) => setPuesto(x.target.value)}
              />
            </label>
          ) : (
            <label className="campo">
              <span className="etiqueta">Titular</span>
              <select
                aria-label="Titular de la caja nueva"
                value={titularId}
                onChange={(x) => setTitularId(x.target.value)}
              >
                <option value="">— elegir —</option>
                {personas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="campo">
            <span className="etiqueta">Código</span>
            <input
              aria-label="Código de la caja nueva"
              placeholder="MESON-2"
              value={codigo}
              onChange={(x) => setCodigo(x.target.value)}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Nombre</span>
            <input
              aria-label="Nombre de la caja nueva"
              placeholder="Mesón 2"
              value={nombre}
              onChange={(x) => setNombre(x.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={
              codigo.trim().length < 2 ||
              nombre.trim().length < 2 ||
              (tipo === 'MESON' && !!puesto.trim() && numero(puesto) === null) ||
              agregar.isPending
            }
            onClick={() => agregar.mutate()}
          >
            Agregar caja
          </button>
        </div>
      )}
      {agregar.isError && <PanelError error={agregar.error} />}
    </section>
  );
}

/** 0133 · orden de la lista: el mesón de cada puesto (1, 2, 3…), los mesones sin puesto y tesorería. */
const ordenCaja = (p: PuntoCobro) => (p.tipo === 'TESORERIA' ? 1000 : p.puesto ? p.puesto : 500);

/** Una tabla de elegibles de una función (responsables u operadoras), con «Retirar». */
function TablaElegibles({
  titulo,
  filas,
  vacia,
  retirando,
  alRetirar,
}: {
  titulo: string;
  filas: ElegibleCaja[];
  vacia: string;
  retirando: boolean;
  alRetirar: (fila: ElegibleCaja) => void;
}) {
  return (
    <table className="grilla compacta" aria-label={titulo}>
      <thead>
        <tr>
          <th>{titulo}</th>
          <th>Elegible desde</th>
          <th>Otorgado por</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {filas.map((v) => (
          <tr key={v.elegibilidadId} data-funcion={v.funcion ?? 'OPERADOR'}>
            <td>
              {v.nombre}
              {v.funcion === 'OPERADOR_EXCEPCIONAL' ? ' · excepcional' : ''}
            </td>
            <td>{fechaHora(v.otorgadoEn)}</td>
            <td>{v.otorgadoPor}</td>
            <td className="al-der">
              <button type="button" disabled={retirando} onClick={() => alRetirar(v)}>
                Retirar
              </button>
            </td>
          </tr>
        ))}
        {!filas.length && (
          <tr>
            <td colSpan={4} className="grilla-vacia">
              {vacia}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function ElegiblesDeCaja({ empresaId, puntoId }: { empresaId: string; puntoId: string }) {
  const qc = useQueryClient();
  const eleg = useElegiblesPunto(empresaId, puntoId);
  const candidatos = useCandidatosCaja(empresaId);
  const [usuarioId, setUsuarioId] = useState('');
  const [funcion, setFuncion] = useState<FuncionElegible>('OPERADOR');
  const refrescar = () => qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
  const vigentes = eleg.data?.vigentes ?? [];
  const responsables = eleg.data?.responsables ?? [];
  const yaTiene =
    funcion === 'RESPONSABLE'
      ? responsables
      : vigentes.filter((v) => (v.funcion ?? 'OPERADOR') === funcion);
  const libres = (candidatos.data ?? []).filter((c) => !yaTiene.some((v) => v.usuarioId === c.id));
  const elegidoCand =
    usuarioId && libres.some((c) => c.id === usuarioId) ? usuarioId : libres[0]?.id || '';
  const agregar = useMutation({
    mutationFn: () =>
      api.post(`/caja/puntos/${puntoId}/elegibles`, { usuarioId: elegidoCand, funcion }),
    onSuccess: async () => {
      setUsuarioId('');
      await refrescar();
    },
  });
  const retirar = useMutation({
    mutationFn: (x: { usuarioId: string; funcion: FuncionElegible }) =>
      api.post(`/caja/puntos/${puntoId}/elegibles/${x.usuarioId}/retirar`, {
        funcion: x.funcion,
      }),
    onSuccess: refrescar,
  });
  return (
    <section className="recuadro" aria-label="Elegibles de la caja">
      <h3>Elegibles de {eleg.data?.punto.codigo ?? 'la caja'}</h3>
      {eleg.isError && <PanelError error={eleg.error} />}
      {eleg.isSuccess && (
        <>
          <TablaElegibles
            titulo="Responsables elegibles"
            filas={responsables}
            vacia="Sin responsables configuradas: cualquiera que abra turnos de caja la abre (como hoy)"
            retirando={retirar.isPending}
            alRetirar={(f) => retirar.mutate({ usuarioId: f.usuarioId, funcion: 'RESPONSABLE' })}
          />
          <TablaElegibles
            titulo="Operadoras elegibles"
            filas={vigentes}
            vacia="Nadie más que la responsable opera esta caja (como hoy)"
            retirando={retirar.isPending}
            alRetirar={(f) =>
              retirar.mutate({ usuarioId: f.usuarioId, funcion: f.funcion ?? 'OPERADOR' })
            }
          />
        </>
      )}
      <div className="acciones">
        <fieldset className="campo" aria-label="Función">
          <legend className="etiqueta">Función</legend>
          {(['OPERADOR', 'OPERADOR_EXCEPCIONAL', 'RESPONSABLE'] as const).map((f) => (
            <label key={f}>
              <input
                type="radio"
                name="funcion-elegible"
                checked={funcion === f}
                onChange={() => setFuncion(f)}
              />{' '}
              {NOMBRE_FUNCION[f]}
            </label>
          ))}
        </fieldset>
        <label className="campo">
          <span className="etiqueta">Agregar persona</span>
          <select
            aria-label="Persona a agregar"
            value={elegidoCand}
            onChange={(e) => setUsuarioId(e.target.value)}
          >
            {libres.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre} · {c.correo}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="primario"
          disabled={!elegidoCand || agregar.isPending}
          onClick={() => agregar.mutate()}
        >
          Dejar elegible
        </button>
      </div>
      <p className="apagado">
        Solo aparecen personas activas con permiso de cobrar en el mesón (para responsable, además
        abrir y cerrar turnos). Retirar a una operadora la saca también del turno abierto de esta
        caja; retirar a una responsable no cierra su turno abierto, pero no abre el siguiente.
      </p>
      {agregar.isError && <PanelError error={agregar.error} />}
      {retirar.isError && <PanelError error={retirar.error} />}
      {!!eleg.data?.historial.length && (
        <details>
          <summary>Historia ({eleg.data.historial.length} retirados)</summary>
          <table className="grilla compacta">
            <tbody>
              {eleg.data.historial.map((h) => (
                <tr key={h.elegibilidadId}>
                  <td>
                    {h.nombre} · {NOMBRE_FUNCION[h.funcion ?? 'OPERADOR'].toLowerCase()}
                  </td>
                  <td>
                    {fechaHora(h.otorgadoEn)} → {h.retiradoEn ? fechaHora(h.retiradoEn) : '—'}
                  </td>
                  <td>
                    otorgó {h.otorgadoPor} · retiró {h.retiradoPor ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}

function PoliticaDeCaja({
  empresaId,
  puntoId,
  puedeEditar,
}: {
  empresaId: string;
  puntoId: string;
  puedeEditar: boolean;
}) {
  const q = usePoliticaCierre(empresaId, puntoId);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return null;
  // La key reinicia el formulario al cambiar de caja o de versión.
  return (
    <FormularioPolitica
      key={`${puntoId}-${q.data.vigente?.politicaId ?? 'sin'}`}
      empresaId={empresaId}
      puntoId={puntoId}
      vigente={q.data.vigente}
      piloto={q.data.pilotoPorDefecto}
      versiones={q.data.versiones.length}
      puedeEditar={puedeEditar}
    />
  );
}

/** Un número entero dentro de un rango (o null). */
const entero = (t: string, min: number, max: number) => {
  const n = Number(t);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

function FormularioPolitica({
  empresaId,
  puntoId,
  vigente,
  piloto,
  versiones,
  puedeEditar,
}: {
  empresaId: string;
  puntoId: string;
  vigente: PoliticaCierre | null;
  /** PILOT_DEFAULT del servidor: la web no tiene umbrales propios. */
  piloto: PoliticaCierre;
  versiones: number;
  puedeEditar: boolean;
}) {
  const qc = useQueryClient();
  const base = vigente ?? piloto;
  const [activa, setActiva] = useState(vigente ? vigente.activa : true);
  const [umbral, setUmbral] = useState(base.umbralMaterial);
  const [opCantidad, setOpCantidad] = useState(String(base.reincidenciaOperadorCantidad));
  const [opDias, setOpDias] = useState(String(base.reincidenciaOperadorDias));
  const [cajaCantidad, setCajaCantidad] = useState(String(base.reincidenciaCajaCantidad));
  const [cajaDias, setCajaDias] = useState(String(base.reincidenciaCajaDias));
  const [anulacionNormal, setAnulacionNormal] = useState(base.anulacionNormal ?? 'NIVEL_1');
  const [umbralAnulacion, setUmbralAnulacion] = useState(base.umbralAnulacionMaterial);
  const [senOperador, setSenOperador] = useState(String(base.senalAnulacionesOperadorCantidad));
  const [senOperadorDias, setSenOperadorDias] = useState(String(base.senalAnulacionesOperadorDias));
  const [senTurno, setSenTurno] = useState(String(base.senalAnulacionesTurnoCantidad));
  const [senCliente, setSenCliente] = useState(String(base.senalAnulacionesClienteCantidad));
  const [senClienteDias, setSenClienteDias] = useState(String(base.senalAnulacionesClienteDias));
  const [escalan, setEscalan] = useState<CodigoSenal[]>(base.senalesQueEscalan ?? []);
  const [causasCorreccion, setCausasCorreccion] = useState<CodigoCausa[]>(
    base.causasCorreccionPermitidas ?? [],
  );
  // OTHER pide texto siempre; los demás códigos, si se marcan aquí.
  const [conTexto, setConTexto] = useState<CodigoCausa[]>(
    base.codigosConTexto?.length ? base.codigosConTexto : ['OTHER'],
  );
  const monto = enteroCaja(umbral);
  const montoAnulacion = enteroCaja(umbralAnulacion);
  const valores = {
    reincidenciaOperadorCantidad: entero(opCantidad, 1, 100),
    reincidenciaOperadorDias: entero(opDias, 1, 366),
    reincidenciaCajaCantidad: entero(cajaCantidad, 1, 100),
    reincidenciaCajaDias: entero(cajaDias, 1, 366),
    senalAnulacionesOperadorCantidad: entero(senOperador, 1, 100),
    senalAnulacionesOperadorDias: entero(senOperadorDias, 1, 366),
    senalAnulacionesTurnoCantidad: entero(senTurno, 1, 100),
    senalAnulacionesClienteCantidad: entero(senCliente, 1, 100),
    senalAnulacionesClienteDias: entero(senClienteDias, 1, 366),
  };
  const valido =
    monto !== null && montoAnulacion !== null && Object.values(valores).every((v) => v !== null);
  const guardar = useMutation({
    mutationFn: () =>
      api.post(`/caja/puntos/${puntoId}/politica-cierre`, {
        activa,
        umbralMaterial: monto!,
        ...valores,
        anulacionNormal,
        umbralAnulacionMaterial: montoAnulacion!,
        senalesQueEscalan: SENALES_ESCALABLES.filter((s) => escalan.includes(s)),
        causasCorreccionPermitidas: (Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).filter((c) =>
          causasCorreccion.includes(c),
        ),
        codigosConTexto: [
          'OTHER',
          ...(Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).filter(
            (c) => c !== 'OTHER' && conTexto.includes(c),
          ),
        ],
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.caja(empresaId) }),
  });
  const campo = (etiqueta: string, valor: string, cambiar: (v: string) => void) => (
    <label className="campo">
      <span className="etiqueta">{etiqueta}</span>
      <input
        aria-label={etiqueta}
        inputMode="numeric"
        value={valor}
        onChange={(e) => cambiar(e.target.value)}
      />
    </label>
  );
  return (
    <section className="recuadro" aria-label="Política de cierre">
      <h3>Política de cierre</h3>
      <p data-politica={vigente ? (vigente.activa ? 'activa' : 'apagada') : 'sin'}>
        {vigente
          ? vigente.activa
            ? `Activa desde ${fechaHora(vigente.creadoEn!)} (${vigente.creadoPor}). Nivel 0: cuadra y sin incidentes, no espera revisión; nivel 1: revisión operativa; nivel 2: aprobación superior.`
            : 'Apagada: tesorería revisa cada cierre de esta caja, como siempre.'
          : 'Sin política: tesorería revisa cada cierre de esta caja, como siempre.'}
        {versiones > 1 ? ` · ${versiones} versiones` : ''}
      </p>
      <fieldset className="caja-politica" disabled={!puedeEditar || guardar.isPending}>
        <label>
          <input type="checkbox" checked={activa} onChange={(e) => setActiva(e.target.checked)} />{' '}
          Controlar los cierres por excepción
        </label>
        <label className="campo">
          <span className="etiqueta">Diferencia material desde (pesos, inclusive) → nivel 2</span>
          <input
            aria-label="Umbral de diferencia material"
            inputMode="numeric"
            className="cobro-monto"
            value={umbral}
            onChange={(e) => setUmbral(e.target.value)}
          />
        </label>
        {campo('Reincidencia por persona: excepciones', opCantidad, setOpCantidad)}
        {campo('… por persona, en estos días móviles', opDias, setOpDias)}
        {campo('Reincidencia de la caja: excepciones', cajaCantidad, setCajaCantidad)}
        {campo('… de la caja, en estos días móviles', cajaDias, setCajaDias)}
        <label className="campo">
          <span className="etiqueta">
            Una anulación normal (con solicitud, con el turno abierto y dentro de la política)
          </span>
          <select
            aria-label="Anulación normal"
            value={anulacionNormal}
            onChange={(e) => setAnulacionNormal(e.target.value as 'NIVEL_1' | 'REVISION_NORMAL')}
          >
            <option value="NIVEL_1">crea una excepción de nivel 1</option>
            <option value="REVISION_NORMAL">queda en la revisión normal (sin excepción)</option>
          </select>
        </label>
        <p className="apagado" data-post-cierre>
          Después del cierre del turno, toda anulación es una excepción de nivel 2
          (POST_CLOSE_VOID_EXCEPTION): no hay plazos de horas.
        </p>
        <label className="campo">
          <span className="etiqueta">Anulación material desde (pesos, inclusive) → nivel 2</span>
          <input
            aria-label="Umbral de anulación material"
            inputMode="numeric"
            className="cobro-monto"
            value={umbralAnulacion}
            onChange={(e) => setUmbralAnulacion(e.target.value)}
          />
        </label>
        {campo('Señal: anulaciones de la misma operadora', senOperador, setSenOperador)}
        {campo('… de la misma operadora, en estos días', senOperadorDias, setSenOperadorDias)}
        {campo('Señal: anulaciones en el turno', senTurno, setSenTurno)}
        {campo('Señal: anulaciones del mismo cliente', senCliente, setSenCliente)}
        {campo('… del mismo cliente, en estos días', senClienteDias, setSenClienteDias)}
        <fieldset className="campo" aria-label="Señales que suben a nivel 2">
          <legend className="etiqueta">
            Señales que suben a nivel 2 (las de regla fija suben siempre)
          </legend>
          {SENALES_ESCALABLES.map((s) => (
            <label key={s}>
              <input
                type="checkbox"
                checked={escalan.includes(s)}
                onChange={(e) =>
                  setEscalan(e.target.checked ? [...escalan, s] : escalan.filter((x) => x !== s))
                }
              />{' '}
              {NOMBRE_SENAL[s]}
            </label>
          ))}
        </fieldset>
        <fieldset className="campo" aria-label="Causas permitidas para corregir después del cierre">
          <legend className="etiqueta">
            Causas permitidas para corregir medios después del cierre
          </legend>
          {(Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).map((c) => (
            <label key={c}>
              <input
                type="checkbox"
                checked={causasCorreccion.includes(c)}
                onChange={(e) =>
                  setCausasCorreccion(
                    e.target.checked
                      ? [...causasCorreccion, c]
                      : causasCorreccion.filter((x) => x !== c),
                  )
                }
              />{' '}
              {NOMBRE_CAUSA[c]}
            </label>
          ))}
        </fieldset>
        <fieldset className="campo" aria-label="Códigos que piden texto">
          <legend className="etiqueta">Al resolver, piden texto</legend>
          {(Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).map((c) => (
            <label key={c}>
              <input
                type="checkbox"
                checked={c === 'OTHER' || conTexto.includes(c)}
                disabled={c === 'OTHER'}
                onChange={(e) =>
                  setConTexto(e.target.checked ? [...conTexto, c] : conTexto.filter((x) => x !== c))
                }
              />{' '}
              {NOMBRE_CAUSA[c]}
            </label>
          ))}
        </fieldset>
      </fieldset>
      <p className="apagado">
        Valores piloto (PILOT_DEFAULT): diferencia material {pesos(piloto.umbralMaterial)};
        anulación material {pesos(piloto.umbralAnulacionMaterial)};{' '}
        {piloto.reincidenciaOperadorCantidad} excepciones en {piloto.reincidenciaOperadorDias} días
        por persona y {piloto.reincidenciaCajaCantidad} en {piloto.reincidenciaCajaDias} por caja.
        Siempre van a nivel 2: tarjeta o cheque que no cuadra, plata de ruta sin entrar al turno, y
        la anulación material, sin solicitud previa, sin venta vinculada, fuera del período
        permitido, con descuadre o de un medio que no cuadra. Las señales son avisos, no fraude.
      </p>
      {puedeEditar && (
        <div className="acciones">
          <button
            type="button"
            className="primario"
            disabled={!valido || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar política'}
          </button>
        </div>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
    </section>
  );
}
